import { Tooltip, Typography } from "@mui/material"
import { TableRecordType } from "../../components/Table"

export const shownSkillsFormatter =
  (remainingSkillsField: string) => (record: TableRecordType, prop: string) => {
    const shownSkills = record[prop]
    const remainingSkills = record[remainingSkillsField]

    if (!remainingSkills) {
      return shownSkills
    }

    return (
      <Tooltip arrow title={remainingSkills}>
        <Typography variant="body2">{shownSkills}</Typography>
      </Tooltip>
    )
  }

export const reportingCategoriesAndStandardsFormatter = (
  record: TableRecordType,
  prop: string,
  context?: { cellValue?: unknown; [key: string]: any }
) => {
  // For nested data (Standards/Categories), use cellValue from context
  // For flat data, use record[prop]
  const value = context?.cellValue ?? record[prop]

  if (!value || value === "null") return "--"

  let itemsArray = []

  if (typeof value === "string") {
    itemsArray = (value as string)?.split(",")?.map(item => item.trim())
  }

  if (Array.isArray(value)) {
    itemsArray = [...value]
  }

  const count = itemsArray?.length || 0

  // No items - show placeholder
  if (count === 0) {
    return <Typography variant="body2">--</Typography>
  }

  // Single item - show it directly
  if (count === 1) {
    return <Typography variant="body2">{itemsArray[0]}</Typography>
  }

  // Multiple items - show first item + count with tooltip showing all
  return (
    <Tooltip arrow title={itemsArray.join(", ")}>
      <Typography variant="body2">{`${itemsArray[0]} + ${
        count - 1
      } more`}</Typography>
    </Tooltip>
  )
}

/**
 * Formatter for standards columns that use standardTreeNames data
 *
 * Parses the standardTreeNames field, groups by framework, and displays
 * standards for the specified framework column
 *
 * @param record - Table record containing standardTreeNames field
 * @param prop - Column property name (e.g., "standards-ACT")
 * @param frameworkName - Framework name to display standards for (e.g., "ACT", "EACT")
 */
export const standardTreeNamesFormatter = (
  record: TableRecordType,
  prop: string,
  frameworkName: string
) => {
  // Use pre-computed grouped standards (parsed once per record in getTransformedTableData)
  const grouped = record["_standardsByFramework"] as
    | Record<string, string[]>
    | undefined

  if (!grouped) {
    return <Typography variant="body2">--</Typography>
  }

  const standardsForFramework = grouped[frameworkName] || []
  const count = standardsForFramework.length

  // No standards for this framework
  if (count < 1) {
    return <Typography variant="body2">--</Typography>
  }

  // Single standard
  if (count === 1) {
    return <Typography variant="body2">{standardsForFramework[0]}</Typography>
  }

  // Multiple standards - show first + count with tooltip
  return (
    <Tooltip arrow title={standardsForFramework.join(", ")}>
      <Typography variant="body2">{`${standardsForFramework[0]} + ${
        count - 1
      } more`}</Typography>
    </Tooltip>
  )
}
